/**
 * LeviDiagnosticsBadge — preview/QA-only build-fingerprint badge +
 * one-tap "Copy Diagnostics" affordance.
 *
 * Purpose (PO 2026-08-09 P0 RCA):
 *   - The Product Owner performs real-iPhone QA repeatedly. When a
 *     Voice / Vision failure occurs, engineering needs the exact
 *     build under test PLUS the current voice/conversation/Vision
 *     state PLUS the current /api/levi/command envelope.
 *   - Existing global `window.__leviQAState` is populated every 500ms
 *     by `useLeviCommand` — this component exposes it via a visible
 *     Copy button so the PO can paste diagnostics into a bug report.
 *
 * Gating:
 *   - Mounted ONLY when the URL carries ?leviDiag=1 (case-insensitive).
 *   - Never visible in normal contractor UX.
 *   - No side effects, no state persistence, no network traffic.
 *   - Redacts auth tokens / API keys / raw customer content (only
 *     IDs and diagnostic metadata are copied).
 */
import { useEffect, useMemo, useState } from "react";
import {
  BUILD_COMMIT,
  BUILD_COMMITTED_AT,
  BUILD_GENERATED_AT,
} from "../../buildInfo.generated";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function _isAppPath() {
  try {
    const p = String(window.location.pathname || "");
    return p.startsWith("/app/") || p === "/app";
  } catch {
    return false;
  }
}

function _isDiagEnabled() {
  try {
    if (typeof window === "undefined") return false;
    // PO 2026-02 · Public marketing badge-leak fix.
    // Every PERSISTENT / STICKY enable path (sessionStorage,
    // localStorage, env flag) requires the caller to be inside the
    // authenticated app surface (``/app/*``). This closes the leak
    // where a visitor who once loaded ``?leviDiag=1`` on any device
    // continued to see the diagnostic badge on public marketing
    // pages via the persisted sticky. Only an EXPLICIT
    // ``?leviDiag=1`` query on the CURRENT URL still activates the
    // badge without an app-path gate — that is the intentional
    // one-shot QA activation flow and remains preserved.
    const qs = window.location.search || "";
    if (/(?:^|[?&])leviDiag=(1|true|yes)(?:&|$)/i.test(qs)) return true;
    // From here down, every enable path also requires ``/app/*``.
    if (!_isAppPath()) return false;
    // PO 2026-02 · Support Diagnostics V1 — contractor-initiated
    // session-scoped enable. Only meaningful inside the authenticated
    // app anyway (the toggle lives on Help & Support).
    try {
      if (window.sessionStorage
          && window.sessionStorage.getItem("jc:support:diag") === "1") {
        return true;
      }
    } catch { /* noop */ }
    // PO 2026-02 · Bounded Preview-only QA instrumentation. Env flag
    // now ALSO requires ``/app/*`` so a Preview build published to
    // Production cannot leak the badge onto the public landing.
    if (String(process.env.REACT_APP_LEVI_VOICE_DIAG_ENABLED || "")
          .toLowerCase() === "true") {
      return true;
    }
    // Sticky-aware (PO 2026-08-15 · Wrapper Voice Boundary iPhone QA)
    // — the sticky persists across SPA navigation for QA runs, but
    // ONLY inside the authenticated app. A stale sticky from a prior
    // QA session no longer surfaces the badge on the public site.
    try {
      if (window.localStorage
          && window.localStorage.getItem("jc:levi:diag") === "1") {
        return true;
      }
    } catch { /* private mode / disabled storage */ }
    return false;
  } catch {
    return false;
  }
}

function _redact(obj) {
  // Shallow-only. `window.__leviQAState` is already tenant-safe.
  if (!obj || typeof obj !== "object") return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (/token|password|secret|api_key/i.test(k)) continue;
    out[k] = v;
  }
  return out;
}

async function _fetchLiveBuildHead() {
  // Ask the backend for the git HEAD it was started against. This is
  // the authoritative deploy-time fingerprint and cannot drift from a
  // stale compile-time constant. Returns null on failure.
  try {
    if (!BACKEND_URL) return null;
    const r = await fetch(`${BACKEND_URL}/api/build/head`, {
      method: "GET",
      headers: { "Accept": "application/json" },
      cache: "no-store",
    });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

async function _collectDiagnostics() {
  const now = new Date().toISOString();
  const state = _redact(
    (typeof window !== "undefined" && window.__leviQAState) || {}
  );
  const parity = _redact(
    (typeof window !== "undefined" && window.__leviParityDiag) || {}
  );
  let backendReadiness = null;
  try {
    if (BACKEND_URL) {
      const r = await fetch(`${BACKEND_URL}/api/`, {
        method: "GET",
        headers: { "Accept": "application/json" },
      });
      backendReadiness = { status: r.status, ok: r.ok };
    }
  } catch (e) {
    backendReadiness = { error: String(e && e.message || e) };
  }
  const live = await _fetchLiveBuildHead();
  return {
    marker: "LEVI_DIAG_2026-08-09",
    captured_at: now,
    build: {
      // Runtime-authoritative (from /api/build/head) is preferred; the
      // compile-time constants are kept as a redundant baseline so
      // any mismatch between them is itself diagnostic evidence.
      commit: (live && live.commit) || BUILD_COMMIT,
      committed_at: (live && live.committed_at) || BUILD_COMMITTED_AT,
      generated_at: BUILD_GENERATED_AT,
      compiled_commit: BUILD_COMMIT,
      compiled_committed_at: BUILD_COMMITTED_AT,
      backend_url: BACKEND_URL,
      href: (typeof window !== "undefined" && window.location.href) || null,
      ua_head: (typeof navigator !== "undefined" && navigator.userAgent
                ? String(navigator.userAgent).slice(0, 240) : null),
    },
    backend_readiness: backendReadiness,
    levi_qa_state: state,
    parity_diag: parity,
  };
}

export default function LeviDiagnosticsBadge() {
  const enabled = useMemo(() => _isDiagEnabled(), []);
  const [copiedAt, setCopiedAt] = useState(null);
  const [expanded, setExpanded] = useState(false);
  // Runtime-authoritative build fingerprint from /api/build/head.
  // Falls back to the compile-time constant on network failure so
  // the badge is never empty. On success this is always the truth.
  const [liveBuild, setLiveBuild] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const live = await _fetchLiveBuildHead();
      if (!cancelled) setLiveBuild(live);
    })();
    try {
      console.log("[LeviDiagnosticsBadge] mounted", {
        compiled_commit: BUILD_COMMIT,
        compiled_committed_at: BUILD_COMMITTED_AT,
      });
    } catch { /* noop */ }
    return () => { cancelled = true; };
  }, [enabled]);
  if (!enabled) return null;

  const displayCommit = (liveBuild && liveBuild.commit) || BUILD_COMMIT;
  const displayCommittedAt =
    (liveBuild && liveBuild.committed_at) || BUILD_COMMITTED_AT;

  const handleCopy = async () => {
    const payload = await _collectDiagnostics();
    const text = JSON.stringify(payload, null, 2);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch { /* noop */ }
        document.body.removeChild(ta);
      }
      setCopiedAt(new Date().toISOString());
    } catch {
      setCopiedAt(null);
    }
  };

  return (
    <div
      data-testid="levi-diagnostics-badge"
      style={{
        position: "fixed",
        // PO 2026-02 · QA-access repositioning. The badge previously
        // sat at bottom-left, which overlapped the DashboardLayout
        // sidebar's Logout / account controls (data-testid=
        // "logout-button") on desktop and hid the mobile bottom nav.
        // Move it to the RIGHT edge so QA operators can always click
        // Logout AND still see the build fingerprint + Copy Diagnostics.
        // Nothing else about the diagnostic surface is changed.
        right: 8,
        bottom: 8,
        zIndex: 2147483000,
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
        fontSize: "11px",
        color: "#fff",
        pointerEvents: "auto",
      }}
    >
      <div
        onClick={() => setExpanded((e) => !e)}
        style={{
          background: "rgba(0,0,0,0.85)",
          border: "1px solid #444",
          borderRadius: 6,
          padding: "6px 10px",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ color: "#4ade80" }}>●</span>
        <span data-testid="levi-diag-fingerprint">
          build {displayCommit}
        </span>
      </div>
      {expanded && (
        <div
          style={{
            marginTop: 6,
            background: "rgba(0,0,0,0.92)",
            border: "1px solid #444",
            borderRadius: 6,
            padding: 10,
            maxWidth: 320,
            lineHeight: 1.35,
            // Expanded panel anchors to the same right edge so it
            // does not spill left over the app content or the
            // sidebar's account/logout area.
            marginLeft: "auto",
          }}
        >
          <div>commit: {displayCommit}</div>
          <div>committed: {displayCommittedAt}</div>
          <div style={{ opacity: 0.7, marginTop: 2 }}>
            compiled: {BUILD_COMMIT}
          </div>
          <div style={{ opacity: 0.7 }}>
            bundle built: {BUILD_GENERATED_AT}
          </div>
          <div style={{ marginTop: 6, opacity: 0.8 }}>
            Reproduce the failure, then tap Copy to grab diagnostics.
          </div>
          <button
            type="button"
            onClick={handleCopy}
            data-testid="levi-diag-copy-btn"
            style={{
              marginTop: 8,
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "6px 10px",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Copy Diagnostics
          </button>
          {copiedAt && (
            <div style={{ marginTop: 6, color: "#4ade80" }}>
              Copied · {copiedAt.slice(11, 19)} UTC
            </div>
          )}
        </div>
      )}
    </div>
  );
}
